import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/motion/Reveal";
import { CTASection } from "@/components/CTASection";
import { SERVICES, PROCESS, slugify } from "@/data/content";

export default function Services() {
  return (
    <div data-testid="services-page">
      <PageHeader
        overline="Services"
        titleLines={["Built to make", "you the best", "in your market."]}
        subtitle="From your first website to full-scale automation and growth strategy — everything we do is engineered to bring your business more customers."
      />

      <section className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="grid gap-6 lg:grid-cols-2">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={(i % 2) * 0.08}>
                <div
                  id={slugify(s.title)}
                  data-testid={`service-card-${i}`}
                  className="group flex h-full scroll-mt-28 flex-col rounded-2xl border border-black/10 bg-paper p-8 transition-transform duration-300 hover:-translate-y-2 hover:border-brand sm:p-10"
                >
                  <div className="flex items-center justify-between">
                    <div className="grid h-14 w-14 place-items-center rounded-xl bg-brand/15 text-brand-dark transition-colors duration-300 group-hover:bg-brand group-hover:text-ink">
                      <s.icon className="h-6 w-6" />
                    </div>
                    <span className="font-mono text-xs uppercase tracking-[0.2em] text-ink/40">
                      {s.tag}
                    </span>
                  </div>
                  <h3 className="mt-7 font-display text-2xl font-bold tracking-tight sm:text-3xl">
                    {s.title}
                  </h3>
                  <p className="mt-4 text-[15px] leading-relaxed text-ink/60">{s.desc}</p>
                  <ul className="mt-6 space-y-3">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-center gap-3 text-sm font-medium text-ink/80">
                        <Check className="h-4 w-4 text-brand" /> {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink py-24 text-white sm:py-32">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand">How it works</p>
            <h2 className="mt-6 max-w-2xl font-display text-4xl font-extrabold leading-[0.95] tracking-tight sm:text-6xl">
              Simple, transparent, built to win.
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS.map((p, i) => (
              <Reveal key={p.no} delay={i * 0.08}>
                <div className="border-t-2 border-brand pt-6">
                  <span className="font-mono text-sm font-bold text-brand">{p.no}</span>
                  <h3 className="mt-3 font-display text-2xl font-bold tracking-tight">{p.title}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-white/55">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/contact"
              data-testid="services-cta"
              className="mt-14 inline-flex items-center gap-2 rounded-full bg-brand px-7 py-4 font-bold text-ink transition-transform hover:-translate-y-0.5 hover:bg-brand-dark hover:text-white"
            >
              Get a free quote <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
